<template>
	<div>
		<loader :dialog="loading"></loader>
		<v-card elevation="2" rounded="xl">
			<div class="cl-header">
				<div class="cl-header-left">
					<div class="cl-icon-box">
						<v-icon size="18" color="white">mdi-account-group</v-icon>
					</div>
					<div>
						<p class="cl-title">Liste des clients</p>
						<p class="cl-subtitle">Gestion de la clientèle</p>
					</div>
				</div>
				<v-text-field
					v-model="search"
					prepend-inner-icon="mdi-magnify"
					placeholder="Rechercher..."
					outlined
					dense
					hide-details
					class="cl-search"
					color="blue darken-2"
				></v-text-field>
			</div>

			<v-divider></v-divider>

			<v-data-table
				:headers="headers"
				:items="clients"
				:server-items-length="totalItems"
				:options.sync="options"
				class="cl-table"
				:loading="loadingTable"
				loading-text="Chargement en cours..."
				:footer-props="{
					'items-per-page-options': [10, 25, 50],
					'items-per-page-all-text': 'Tout',
					'items-per-page-text': 'Lignes par page',
				}"
			>
				<template v-slot:[`item.fullName`]="{ item }">
					<button class="cl-name-btn" @click="showCustomerDetails(item)">
						{{ item.fullName }}
					</button>
				</template>

				<template v-slot:[`item.weight`]="{ item }">
					<v-chip small :color="getWeightColor(item.weight)" dark>
						{{ item.weight }} kg
					</v-chip>
				</template>

				<template v-slot:[`item.birthday`]="{ item }">
					<span>{{ moment().diff(item.birthday, "years") }} ans</span>
				</template>

				<template v-slot:[`item.salle`]="{ item }">
					<v-chip
						v-if="item.salle && item.salle.name"
						small
						color="#eff6ff"
						text-color="#1565c0"
						class="salle-chip"
					>{{ item.salle.name }}</v-chip>
					<span v-else class="text-caption grey--text">—</span>
				</template>

				<template v-slot:[`item.subscriptionCreatedAt`]="{ item }">
					<span v-if="item.inPause">
						<v-chip small color="orange" dark>En pause</v-chip>
					</span>
					<v-tooltip bottom v-else-if="item.subscription">
						<template v-slot:activator="{ on, attrs }">
							<v-progress-linear
								v-bind="attrs"
								v-on="on"
								:color="getProgressionColor(item.subscriptionCreatedAt, item.subscription.duration)"
								:value="100 - ((item.subscription.duration - moment().diff(moment(item.subscriptionCreatedAt), 'days') - 1) / item.subscription.duration) * 100"
								height="6"
								rounded
								style="max-width: 120px;"
							></v-progress-linear>
						</template>
						<span>
							{{ item.subscription.duration - moment().diff(moment(item.subscriptionCreatedAt), "days") - 1 }}
							jours restants — {{ item.subscription.type }}
						</span>
					</v-tooltip>
					<span v-else class="text-caption grey--text">—</span>
				</template>

				<template v-slot:[`footer.page-text`]="items">
					{{ items.pageStart }}-{{ items.pageStop }} sur {{ items.itemsLength }}
				</template>
				<template v-slot:no-data>
					<v-btn text color="primary" @click="initialize">Actualiser</v-btn>
				</template>
			</v-data-table>
		</v-card>

		<!-- Customer details dialog -->
		<v-dialog v-model="customerDetailsDialog" max-width="480px">
			<customer-details
				:customer="client"
				@close-customer-details-dialog="customerDetailsDialog = false"
			></customer-details>
		</v-dialog>

		<!-- Foods dialog -->
		<v-dialog v-model="foodDialog" max-width="520px">
			<v-card rounded="xl" class="pa-2">
				<div class="sub-dialog-header">
					<div class="sub-icon-box" style="background:#00695c;">
						<v-icon size="16" color="white">mdi-food</v-icon>
					</div>
					<p class="sub-dialog-title">Liste des repas</p>
					<v-spacer></v-spacer>
					<v-btn icon small @click="foodDialog = false">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pa-3">
					<food :foods="food"></food>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Protocols dialog -->
		<v-dialog scrollable v-model="protocolDialog" max-width="520px">
			<protocoles
				@close-protocol-dialog="protocolDialog = false"
				:protocols="protocols"
			></protocoles>
		</v-dialog>

		<!-- Subscriptions dialog -->
		<v-dialog v-model="subscriptionDialog" max-width="520px">
			<v-card rounded="xl" class="pa-2">
				<div class="sub-dialog-header">
					<div class="sub-icon-box" style="background:#1565c0;">
						<v-icon size="16" color="white">mdi-playlist-check</v-icon>
					</div>
					<p class="sub-dialog-title">Abonnement</p>
					<v-spacer></v-spacer>
					<v-btn icon small @click="subscriptionDialog = false">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pa-3">
					<subscriptions :client="subscription"></subscriptions>
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Rewards dialog -->
		<v-dialog v-model="rewardDialog" max-width="520px" persistent scrollable>
			<v-card rounded="xl" class="pa-2">
				<div class="sub-dialog-header">
					<div class="sub-icon-box" style="background:#f59e0b;">
						<v-icon size="16" color="white">mdi-trophy</v-icon>
					</div>
					<p class="sub-dialog-title">Liste des récompenses</p>
					<v-spacer></v-spacer>
					<v-btn icon small @click="rewardDialog = false">
						<v-icon color="#6b7280">mdi-close</v-icon>
					</v-btn>
				</div>
				<v-divider></v-divider>
				<v-card-text class="pa-3">
					<reward
						:rewards="rewards"
						:customerId="clientId"
						@close-reward-dialog="rewardDialog = false"
					></reward>
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
import moment from "moment";
import service from "../services";
import Loader from "@/components/Loader.vue";
import Food from "@/components/Admin/Customers/Food.vue";
import Reward from "@/components/Admin/Customers/Reward.vue";
import Protocoles from "@/components/Admin/Customers/Protocoles.vue";
import Subscriptions from "@/components/Admin/Customers/Subscriptions.vue";
import CustomerDetails from "@/components/Admin/Customers/CustomerDetails.vue";
export default {
	components: { Food, Protocoles, Subscriptions, Loader, Reward, CustomerDetails },
	data() {
		return {
			moment,
			rewardDialog: false,
			rewards: [],
			search: "",
			searchTimeout: null,
			foodDialog: false,
			protocolDialog: false,
			subscriptionDialog: false,
			customerDetailsDialog: false,
			food: [],
			protocols: [],
			subscription: {},
			loadingTable: false,
			loading: false,
			options: {},
			totalItems: 0,
			headers: [
				{ text: "Nom", align: "start", sortable: false, value: "fullName" },
				{ text: "Âge", value: "birthday", sortable: false },
				{ text: "Sexe", value: "sexe", sortable: false },
				{ text: "Poids", value: "weight", sortable: false },
				{ text: "Objectif", value: "objectif", sortable: false },
				{ text: "Salle", value: "salle", sortable: false },
				{ text: "Abonnement", value: "subscriptionCreatedAt", sortable: false },
			],
			clients: [],
			clientId: "",
			client: {},
		};
	},
	watch: {
		options: {
			handler() { this.initialize(); },
			deep: true,
		},
		search() {
			clearTimeout(this.searchTimeout);
			this.searchTimeout = setTimeout(() => {
				if (this.options.page !== 1) {
					this.options.page = 1;
				} else {
					this.initialize();
				}
			}, 400);
		},
	},
	methods: {
		initialize() {
			this.loadingTable = true;
			const page = this.options.page || 1;
			const limit = this.options.itemsPerPage || 10;
			service.getClientsPaginated({ page, limit, search: this.search })
				.then((r) => { this.clients = r.data.customers; this.totalItems = r.data.totalPages * limit; this.loadingTable = false; })
				.catch((e) => { console.log(e); this.loadingTable = false; });
		},
		showReward(clientId) {
			this.loading = true;
			service.getRewards().then((r) => { this.rewards = r.data; this.clientId = clientId; this.rewardDialog = true; this.loading = false; }).catch((e) => console.log(e));
		},
		showCustomerDetails(client) { this.customerDetailsDialog = true; this.client = client; },
		showSubscription(client) { this.subscriptionDialog = true; this.subscription = client.subscription ? client : "Aucun abonnement attribué"; },
		showFoods(client) {
			this.loading = true;
			service.getCustomerFood(client._id).then((r) => { this.food = r.data; this.loading = false; this.foodDialog = true; }).catch((e) => { console.log(e); this.loading = false; });
		},
		showProtocol(clientId) {
			this.protocolDialog = true;
			this.loading = true;
			service.getProtocol(clientId).then((r) => { this.protocols = r.data; this.loading = false; }).catch((e) => { console.log(e); this.loading = false; });
		},
		getWeightColor(weight) { if (weight < 80) return "green"; else if (weight > 100) return "red"; else return "orange"; },
		getProgressionColor(subscriptionCreatedAt, duration) {
			const diff = moment().diff(moment(subscriptionCreatedAt), "days");
			if (diff < duration - 11) return "primary";
			else if (diff < duration) return "yellow";
			else return "red";
		},
	},
};
</script>

<style scoped>
.cl-header { display: flex; align-items: center; padding: 18px 20px 14px; gap: 14px; flex-wrap: wrap; }
.cl-header-left { display: flex; align-items: center; gap: 14px; flex: 1; min-width: 200px; }
.cl-icon-box { width: 38px; height: 38px; border-radius: 10px; background: #1565c0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.cl-title { font-size: 0.92rem; font-weight: 700; color: #111827; margin: 0 0 2px; font-family: Montserrat, sans-serif; }
.cl-subtitle { font-size: 0.72rem; color: #9ca3af; margin: 0; font-family: Montserrat, sans-serif; }
.cl-search { max-width: 260px; }
.cl-table { font-family: Montserrat, sans-serif; }
.cl-name-btn { background: none; border: none; cursor: pointer; font-family: Montserrat, sans-serif; font-size: 0.83rem; color: #111827; padding: 0; text-align: left; }
.cl-name-btn:hover { text-decoration: underline; color: #374151; }
.sub-dialog-header { display: flex; align-items: center; gap: 12px; padding: 16px 20px 12px; }
.sub-icon-box { width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sub-dialog-title { font-size: 0.88rem; font-weight: 700; color: #111827; margin: 0; font-family: Montserrat, sans-serif; }
.salle-chip { font-size: 0.78rem !important; font-family: Montserrat, sans-serif; font-weight: 600; }
</style>
